import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('DashboardsService');

const baseUrl = environment.services.account.baseUrl;
const dashboardsUrl = baseUrl + environment.services.account.basePath;

export interface Category {
  deactivated?: boolean;
  id: number;
  lastUpdated?: string;
  name: string;
  orderBy: number;
  shortname?: string;
  subtitle?: string;
}

export interface Dashboard {
  accessOnly: boolean;
  category: Category;
  deactivated: boolean;
  id: number;
  name: string;
  orderBy: number;
  shortName: string;
  subtitle: string;
  demo: string;
  downtime: string;
  icon: string;
  internalOnly: boolean;
  listenReporting: boolean;
  hxcProgram: boolean;
  placeholder: boolean;
  resource: string;
  embedId: string;
  superscript: string;
  thumbnail: string;
  type: string; // Superset, Redirect
  updateDate?: string | undefined;
}

class DashboardsService {
  /**
   * Fetch dashboard categories from the API
   */
  async getDashboardCategories(showAll: boolean = false): Promise<Category[]> {
    try {
        const queryParams = showAll ? '?showAll=true' : '';
      const url = `${dashboardsUrl}/portal/dashboard/categories${queryParams}`;
      log.log(`Fetching dashboard categories from: ${url}`);
      const response = await apiService.makeRequest('get', url);
      return response.categories || [];
    } catch (error) {
      log.error('Error fetching dashboard categories:', error);
      throw error;
    }
  }

  /**
   * Fetch dashboards from the API
   */
  async getDashboards(showAll: boolean = false): Promise<Dashboard[]> {
    try {
        const queryParams = showAll ? '?showAll=true' : '';
      const url = `${dashboardsUrl}/portal/dashboard/options${queryParams}`;
      log.log(`Fetching dashboards from: ${url}`);
      const response = await apiService.makeRequest('get', url);
      return response.dashboards || [];
    } catch (error) {
      log.error('Error fetching dashboards:', error);
      throw error;
    }
  }

  /**
   * Update a dashboard category
   */
  async updateCategory(category: Category): Promise<Category> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/categories/${category.id}`;
      log.log(`Updating category with ID ${category.id}`);
      const response = await apiService.makeRequest('put', url, category);
      return response.categories || category;
    } catch (error) {
      log.error(`Error updating category with ID ${category.id}:`, error);
      throw error;
    }
  }

  /**
   * Deactivate a dashboard category (soft delete)
   */
  async deactivateCategory(categoryId: number): Promise<void> {
    if (!categoryId || categoryId <= 0) {
      throw new Error(`Invalid category ID: ${categoryId}`);
    }

    try {
      const url = `${dashboardsUrl}/portal/dashboard/categories/${categoryId}`;
      log.log(`Deactivating category with ID ${categoryId}`);
      await apiService.makeRequest('delete', url);
      log.log(`Category with ID ${categoryId} deactivated successfully`);
    } catch (error) {
      log.error(`Error deactivating category with ID ${categoryId}:`, error);
      throw new Error(`Failed to deactivate category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create a new dashboard category
   */
  async createCategory(category: Omit<Category, 'id'>): Promise<Category> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/categories`;
      log.log('Creating new dashboard category');
      const response = await apiService.makeRequest('post', url, category);
      return response;
    } catch (error) {
      log.error('Error creating dashboard category:', error);
      throw error;
    }
  }

  /**
   * Update a dashboard
   */
  async updateDashboard(dashboard: Dashboard): Promise<Dashboard> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/options/${dashboard.id}`;
      log.log(`Updating dashboard with ID ${dashboard.id}, deactivated status: ${dashboard.deactivated}`);

      // Create proper object structure expected by API
      const dashboardToSend = {
        ...dashboard,
        // Send the category ID instead of the category name
        category: dashboard.category?.name || '',
        // Explicitly include deactivated flag to ensure it's sent to the server
        deactivated: dashboard.deactivated
      };

      // Log the data being sent for debugging
      log.log(`Sending dashboard update with deactivated: ${dashboardToSend.deactivated}`);

      const response = await apiService.makeRequest('put', url, dashboardToSend);
      return response.dashboards || dashboard;
    } catch (error) {
      log.error(`Error updating dashboard with ID ${dashboard.id}:`, error);
      throw error;
    }
  }

  /**
   * Deactivate a dashboard (soft delete)
   */
  async deactivateDashboard(dashboardId: number): Promise<void> {
    if (!dashboardId || dashboardId <= 0) {
      throw new Error(`Invalid dashboard ID: ${dashboardId}`);
    }

    try {
      const url = `${dashboardsUrl}/portal/dashboard/options/${dashboardId}`;
      log.log(`Deactivating dashboard with ID ${dashboardId}`);
      await apiService.makeRequest('delete', url);
      log.log(`Dashboard with ID ${dashboardId} deactivated successfully`);
    } catch (error) {
      log.error(`Error deactivating dashboard with ID ${dashboardId}:`, error);
      throw new Error(`Failed to deactivate dashboard: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create a new dashboard
   */
  async createDashboard(dashboard: Partial<Dashboard>): Promise<Dashboard> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/options`;
      log.log('Creating new dashboard');
      
      // Create a modified dashboard object with category ID instead of category object
      const dashboardToSend = {
        ...dashboard,
        category: dashboard.category?.name || ''  // Send category ID as a string
      };
      
      const response = await apiService.makeRequest('post', url, dashboardToSend);
      return response.dashboards || []; // Return the full response which should contain the created dashboard
    } catch (error) {
      log.error('Error creating dashboard:', error);
      throw error;
    }
  }

  /**
   * Get a specific dashboard by ID
   */
  async getDashboardById(id: number): Promise<Dashboard | null> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/options/${id}`;
      log.log(`Fetching dashboard with ID ${id}`);
      const response = await apiService.makeRequest('get', url);
      return response.dashboard || null;
    } catch (error) {
      log.error(`Error fetching dashboard with ID ${id}:`, error);
      // Fallback to fetching all dashboards and filtering
      try {
        const dashboards = await this.getDashboards();
        return dashboards.find(dash => dash.id === id) || null;
      } catch (fallbackError) {
        log.error('Fallback fetching also failed:', fallbackError);
        return null;
      }
    }
  }

  /**
   * Get the current site downtime value
   */
  async getSiteDowntime(): Promise<string | null> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/access`;
      log.log('Fetching site downtime');
      const response = await apiService.makeRequest('get', url);
      return response.siteDowntime ?? null;
    } catch (error) {
      log.error('Error fetching site downtime:', error);
      throw error;
    }
  }

  /**
   * Set or clear site downtime.
   * Pass an ISO 8601 string to set, or null to clear.
   */
  async setSiteDowntime(downtime: string | null): Promise<void> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/setSiteDowntime`;
      const body = { downtime: downtime ? new Date(downtime).toISOString() : null };
      log.log(`Setting site downtime to: ${body.downtime}`);
      await apiService.makeRequest('put', url, body);
    } catch (error) {
      log.error('Error setting site downtime:', error);
      throw error;
    }
  }

  /**
   * Get a specific category by ID
   */
  async getCategoryById(id: number): Promise<Category | null> {
    try {
      const url = `${dashboardsUrl}/portal/dashboard/categories/${id}`;
      log.log(`Fetching category with ID ${id}`);
      const response = await apiService.makeRequest('get', url);
      return response.category || null;
    } catch (error) {
      log.error(`Error fetching category with ID ${id}:`, error);
      // Fallback to fetching all categories and filtering
      try {
        const categories = await this.getDashboardCategories();
        return categories.find(cat => cat.id === id) || null;
      } catch (fallbackError) {
        log.error('Fallback fetching also failed:', fallbackError);
        return null;
      }
    }
  }
}

export const dashboardsService = new DashboardsService();
export default dashboardsService;